import { ReactNode } from 'react'

type GuideStep = {
  /** アンカー。/guide#b-1 のように手順を直接指せる */
  id: string
  /** 見出しの文言そのまま（末尾の {#id} 指定は除く）。目次に出す */
  title: string
}

export type GuideSection = {
  /** アンカー。/guide#a のように紙・受電の台本から直接指せる */
  id: string
  /** A〜D の区分記号。紙のシートと逐語一致させる */
  label?: string
  /** 目次・見出しに出す文言 */
  title: string
  body: string
  /** 本文の ### 見出しから機械的に起こした手順一覧（目次の2階層目） */
  steps: GuideStep[]
}

/**
 * 法人別 variant（variants/ の各ファイルが 1 法人ずつ定義する）。
 * まもる君の /guide と同じ作り (prescription-management-front src/pages/guide)。
 * 本文は法人で変えず、連絡先だけを variant が持つ。
 * /guide は未認証でも開けるため、閲覧者で動的に分岐はしない。
 */
export type GuideVariant = {
  sections: GuideSection[]
  /** 「それでも解決しないとき」冒頭の連絡先。法人ごとに違うため md ではなく variant が持つ */
  contact: ReactNode
}

/** 見出しの末尾に置く明示アンカー。`### 設定の調整 {#tuning-2}` */
const EXPLICIT_ID = /\s*\{#([a-z0-9-]+)\}\s*$/

/**
 * 見出しの文言からアンカーを導く。
 *
 * 目次と本文で id を突き合わせる必要があるが、文言を variant 定義と md に
 * 二重で持つと必ずずれる。md の見出しを唯一の出所とし、双方がこの関数で
 * 同じ id を導出する。出現順の連番には依存させない（StrictMode の二重
 * レンダで狂うため）。
 *
 * - `A-1.` のような区分記号 → `a-1`
 * - 記号を持たない見出し（「生成を好みに近づけるには」の下）は `{#id}` で明示する
 */
export const headingId = (heading: string): string => {
  const explicit = heading.match(EXPLICIT_ID)
  if (explicit) return explicit[1]

  const step = heading.match(/^([A-E])-(\d+)\./)
  if (step) return `${step[1].toLowerCase()}-${step[2]}`

  return ''
}

/** 表示用の見出し文言。`{#id}` の指定を落とす */
export const headingTitle = (heading: string): string =>
  heading.replace(EXPLICIT_ID, '').trim()

/**
 * 本文の ### 見出しから手順一覧を起こす。
 *
 * id を持てない見出しは目次にもアンカーにも出せない。ただし本文には出て
 * しまうので、黙って落とすと「書いたのに目次に無い」に気づけない。
 * 目次がこのページの骨格である以上、落ちたことは開発時に見えていないといけない。
 */
const extractSteps = (body: string): GuideStep[] =>
  [...body.matchAll(/^### (.+)$/gm)]
    .map(([, heading]) => {
      const id = headingId(heading.trim())
      if (!id && import.meta.env.DEV) {
        console.warn(
          `[guide] 見出し「${heading.trim()}」は記号（A-1.）でも {#id} でも始まらないため、目次にもアンカーにも出ません。`
        )
      }
      return { id, title: headingTitle(heading) }
    })
    .filter((step) => Boolean(step.id))

export const section = (s: Omit<GuideSection, 'steps'>): GuideSection => ({
  ...s,
  steps: extractSteps(s.body),
})
